import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { LoginProjectConfiguration, setWelcomeEmail } from '../../../api';
import {
  ConfigurationSample,
  FieldBlock,
  FieldSwitch,
  Preloader,
  StyledLinkUnderline,
  ToDashboardError
} from '../../../components/ui';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';

type RouteParams = { projectId: string; confPage: string };

const SignUpSettingsPage = ({ match }: RouteComponentProps): JSX.Element | null => {
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;
  const {
    currentProjectFetching,
    currentProjectType,
    currentProject,
    updateCurrentProject,
    currentUpdateFetching,
    error
  } = useCurrentProject(projectId);
  const { socialConnectionPath } = usePaths();
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [welcomeEmailToggle, setWelcomeEmailToggle] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  if (error) {
    return <ToDashboardError error={error} pageName="sign-up-settings_open" />;
  }

  if (!currentProject) {
    return <Preloader />;
  }

  const onSubmit = async (values: LoginProjectConfiguration) => {
    try {
      setSubmitError(false);
      await Promise.all([
        updateCurrentProject(values),
        welcomeEmailToggle ? setWelcomeEmail(projectId) : null
      ]);
      showSuccessNotify();
    } catch (err) {
      showErrorNotify();
      setSubmitError(true);
    }
  };

  return (
    <ConfigurationSample
      currentProject={currentProject}
      currentProjectType={currentProjectType}
      currentProjectFetching={currentProjectFetching}
      currentUpdateFetching={currentUpdateFetching}
      title={intl.formatMessage({ id: 'configuration-page.sign-up.page-title' })}
      description={intl.formatMessage({ id: 'configuration-page.sign-up.page-description' })}
      notification={intl.formatMessage(
        {
          id: 'configuration-page.sign-up.page-notification'
        },
        {
          a: (chunk: string) => (
            <StyledLinkUnderline to={socialConnectionPath(projectId)}>{chunk}</StyledLinkUnderline>
          )
        }
      )}
      onSubmit={onSubmit}
      submitError={submitError}>
      <>
        <FieldBlock>
          <FieldSwitch
            name="registration_allowed"
            caption={intl.formatMessage({
              id: 'configuration-page.sign-up.disable-sign-up.description'
            })}
            checked={currentProject.registration_allowed}
            label={intl.formatMessage({
              id: 'configuration-page.sign-up.disable-sign-up.title'
            })}
          />
        </FieldBlock>
        <FieldBlock>
          <FieldSwitch
            name="welcome_email"
            caption={intl.formatMessage({
              id: 'configuration-page.sign-up.welcome-email.description'
            })}
            checked={welcomeEmailToggle}
            label={intl.formatMessage({
              id: 'configuration-page.sign-up.welcome-email.title'
            })}
            onClick={(event) => setWelcomeEmailToggle((event.target as HTMLInputElement).checked)}
          />
        </FieldBlock>
      </>
    </ConfigurationSample>
  );
};

export default withRouter(SignUpSettingsPage);
